import * as React from 'react';
import type { TreeItem } from './types';

type HandleSelect = (
  item: TreeItem,
  options?: { skipExpandHandling?: boolean },
) => void;

interface NodeInfo {
  parentId: string | null;
  depth: number;
  node: TreeItem;
}

interface UseTreeFocusProps {
  items: TreeItem[];
  expandedSet: Set<string>;
  currentSelectedId: string | null;
  expandItem: (id: string) => void;
  toggleItem: (id: string) => void;
  handleSelect: HandleSelect;
}

export const useTreeFocus = ({
  items,
  expandedSet,
  currentSelectedId,
  expandItem,
  toggleItem,
  handleSelect,
}: UseTreeFocusProps) => {
  const itemRefs = React.useRef(new Map<string, HTMLDivElement | null>());

  const { visibleNodeIds, nodeInfo } = React.useMemo(() => {
    const ids: string[] = [];
    const info = new Map<string, NodeInfo>();

    const traverse = (
      nodes: TreeItem[],
      parentId: string | null,
      depth: number,
    ) => {
      for (const node of nodes) {
        info.set(node.id, { parentId, depth, node });
        ids.push(node.id);

        if (node.children?.length && expandedSet.has(node.id)) {
          traverse(node.children, node.id, depth + 1);
        }
      }
    };

    traverse(items, null, 0);

    return { visibleNodeIds: ids, nodeInfo: info };
  }, [expandedSet, items]);

  const focusTargetId = React.useMemo(() => {
    if (currentSelectedId && visibleNodeIds.includes(currentSelectedId)) {
      return currentSelectedId;
    }

    const firstEnabledId = visibleNodeIds.find(
      (id) => !nodeInfo.get(id)?.node.disabled,
    );
    return firstEnabledId ?? visibleNodeIds[0] ?? null;
  }, [currentSelectedId, nodeInfo, visibleNodeIds]);

  const focusNode = React.useCallback((id: string | null) => {
    if (!id) return;
    const element = itemRefs.current.get(id);
    element?.focus();
  }, []);

  const focusSibling = React.useCallback(
    (currentId: string, delta: number) => {
      const currentIndex = visibleNodeIds.indexOf(currentId);
      if (currentIndex === -1) return;

      const nextIndex = currentIndex + delta;
      if (nextIndex < 0 || nextIndex >= visibleNodeIds.length) return;

      focusNode(visibleNodeIds[nextIndex]);
    },
    [focusNode, visibleNodeIds],
  );

  const focusParent = React.useCallback(
    (currentId: string) => {
      const parentId = nodeInfo.get(currentId)?.parentId;
      if (parentId) {
        focusNode(parentId);
      }
    },
    [focusNode, nodeInfo],
  );

  const focusFirstChild = React.useCallback(
    (nodeId: string) => {
      const currentIndex = visibleNodeIds.indexOf(nodeId);
      if (currentIndex === -1) return;

      const potentialChildId = visibleNodeIds[currentIndex + 1];
      if (!potentialChildId) return;

      if (nodeInfo.get(potentialChildId)?.parentId === nodeId) {
        focusNode(potentialChildId);
      }
    },
    [focusNode, nodeInfo, visibleNodeIds],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, node: TreeItem) => {
      const hasChildren = !!node.children?.length;
      const isExpanded = expandedSet.has(node.id);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusSibling(node.id, 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          focusSibling(node.id, -1);
          break;
        case 'ArrowRight':
          if (!hasChildren) break;
          event.preventDefault();
          if (node.disabled) break;
          if (!isExpanded) {
            expandItem(node.id);
          } else {
            focusFirstChild(node.id);
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (node.disabled) {
            focusParent(node.id);
            break;
          }
          if (hasChildren && isExpanded) {
            toggleItem(node.id);
          } else {
            focusParent(node.id);
          }
          break;
        case 'Home':
          event.preventDefault();
          focusNode(visibleNodeIds[0] ?? null);
          break;
        case 'End':
          event.preventDefault();
          focusNode(visibleNodeIds[visibleNodeIds.length - 1] ?? null);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          if (!node.disabled) {
            handleSelect(node);
          }
          break;
        default:
          break;
      }
    },
    [
      expandItem,
      focusFirstChild,
      focusNode,
      focusParent,
      focusSibling,
      handleSelect,
      toggleItem,
      visibleNodeIds,
      expandedSet,
    ],
  );

  return {
    focusTargetId,
    handleKeyDown,
    itemRefs,
  };
};
